import { createRootRouteWithContext, Outlet, useRouterState, Link } from '@tanstack/react-router';
import { AnimatePresence } from 'motion/react';
import type { QueryClient } from '@tanstack/react-query';

import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { ChatWidget } from '@/components/chat/chat-widget';
import { PageTransition } from '@/components/motion/primitives';
import { Button } from '@/components/ui/button';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  // Key the transition on pathname so AnimatePresence sees a new child on
  // navigation. Keying on the full location would also re-animate on a mere
  // search-param change, which makes filtering the shop flicker.
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <div className="flex min-h-dvh flex-col">
      {/* First tab stop on every page. Invisible until focused. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-100 focus:rounded-lg focus:bg-farm-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>

      <Header />

      {/*
        tabIndex={-1} is what makes the skip link above actually skip.
        An <a href="#main"> scrolls the target into view but does NOT move
        focus to it unless the target is focusable — so without this, a
        keyboard user activates "Skip to content", focus stays on the link,
        and the next Tab walks them right back through the header nav. The
        link looks like it works and doesn't. -1 keeps it out of the tab
        order while still allowing programmatic focus.
      */}
      <main id="main" tabIndex={-1} className="flex-1 pt-18 focus:outline-none">
        <AnimatePresence mode="wait">
          <PageTransition key={pathname}>
            <Outlet />
          </PageTransition>
        </AnimatePresence>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
}

function NotFound() {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-4 text-center">
      <p className="font-display text-7xl font-black text-gradient-brand">404</p>
      <h1 className="mt-4 font-display text-2xl font-bold text-soil-900">Nothing growing here</h1>
      <p className="mt-2 text-soil-600">
        This page doesn't exist — or it was harvested. Either way, the good stuff is elsewhere.
      </p>
      <div className="mt-8 flex gap-3">
        <Link to="/">
          <Button>Back home</Button>
        </Link>
        <Link to="/shop">
          <Button variant="outline">Browse produce</Button>
        </Link>
      </div>
    </div>
  );
}
